package no.vareflow.app.domain

import kotlin.random.Random

/**
 * Login for people without an e-mail address: Firmakode + brukernavn + passord.
 *
 * Firebase Authentication needs an e-mail address, so such a person gets an internal one that is
 * never shown and never receives mail: `peter@salt-pepper.users.vareflow.no`. The company id (not
 * the Firmakode) is part of it, so the same brukernavn can exist in two companies and a changed
 * Firmakode does not lock anyone out.
 */
object CompanyLogin {
    const val USER_DOMAIN = "users.vareflow.no"
    const val MIN_USERNAME = 2
    const val MAX_USERNAME = 30
    /** Firebase needs at least 6 characters; we ask for 8. */
    const val MIN_PASSWORD = 8
    private const val START_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"

    /** " Per Øyvind " → "per.oyvind"; only a–z, 0–9, dot, dash and underscore remain. */
    fun normalizeUsername(input: String): String =
        input.trim().lowercase()
            .replace("æ", "ae").replace("ø", "o").replace("å", "a")
            .replace("ä", "a").replace("ö", "o").replace("ü", "u").replace("é", "e")
            .replace(Regex("\\s+"), ".")
            .filter { it in 'a'..'z' || it in '0'..'9' || it == '.' || it == '-' || it == '_' }
            .trim('.', '-', '_')

    /** Why [username] (already normalized) cannot be used, or null when it is fine. */
    fun usernameProblem(username: String): String? = when {
        username.length < MIN_USERNAME -> t("Brukernavnet må ha minst $MIN_USERNAME tegn.", "The username must have at least $MIN_USERNAME characters.")
        username.length > MAX_USERNAME -> t("Brukernavnet kan ha høyst $MAX_USERNAME tegn.", "The username can have at most $MAX_USERNAME characters.")
        !username.first().isLetterOrDigit() -> t("Brukernavnet må starte med en bokstav eller et tall.", "The username must start with a letter or a digit.")
        else -> null
    }

    /** The internal e-mail used with Firebase for brukernavn [username] in company [companyId]. */
    fun syntheticEmail(username: String, companyId: String): String {
        val company = companyId.lowercase().replace(Regex("[^a-z0-9-]+"), "-").trim('-').ifBlank { "bedrift" }
        return "${normalizeUsername(username)}@$company.$USER_DOMAIN"
    }

    fun isSyntheticEmail(email: String): Boolean = email.trim().lowercase().endsWith(".$USER_DOMAIN")

    /** What to show for a login name: the brukernavn for internal addresses, else the e-mail. */
    fun displayLogin(email: String): String =
        if (isSyntheticEmail(email)) email.substringBefore('@') else email

    /**
     * Start code the admin gives a new e-mail-less employee, e.g. "K7MP-4XQ2": 8 characters
     * without 0/O and 1/I. The employee must choose an own password at the first login.
     */
    fun startCode(random: Random = Random.Default): String {
        val chars = (1..8).map { START_CODE_ALPHABET[random.nextInt(START_CODE_ALPHABET.length)] }.joinToString("")
        return chars.substring(0, 4) + "-" + chars.substring(4)
    }

    /** Why [password] cannot be the new own password, or null when it is fine. */
    fun newPasswordProblem(password: String, repeat: String, startCode: String = ""): String? = when {
        password.length < MIN_PASSWORD -> t("Passordet må ha minst $MIN_PASSWORD tegn.", "The password must have at least $MIN_PASSWORD characters.")
        password != repeat -> t("Passordene er ikke like.", "The passwords do not match.")
        startCode.isNotBlank() && password == startCode -> t("Velg et annet passord enn startkoden.", "Choose a password other than the start code.")
        else -> null
    }
}
